import { useState } from 'react';
import { Dialog } from '@/components/Base/Headless';
import Lucide from '@/components/Base/Lucide';

type Category = 'Performance' | 'Trend' | 'Fix';

interface SuggestedPrompt {
  category: Category;
  title: string;
  description: string;
  prompt: string;
}

const CATEGORY_STYLES: Record<Category, string> = {
  Performance: 'bg-indigo-50 text-indigo-600',
  Trend: 'bg-emerald-50 text-emerald-700',
  Fix: 'bg-orange-50 text-orange-700',
};

const SUPEROWNER_PROMPTS: SuggestedPrompt[] = [
  {
    category: 'Performance',
    title: 'Results since go-live',
    description: 'Summary of leads, tours, attendance and sales since Pulse went live.',
    prompt: 'Give me a summary of leads, tours booked, tours attended and sales since go-live.',
  },
  {
    category: 'Trend',
    title: 'Best lead source',
    description: 'The lead source that performs best across the business.',
    prompt: 'Which lead source performs best across the business?',
  },
  {
    category: 'Fix',
    title: 'Critical knowledge gaps',
    description: 'Questions the AI could not answer that led to a lost booking, ranked by impact.',
    prompt: 'Which questions could the AI not answer that led to a lost booking? Rank them by impact.',
  },
  {
    category: 'Trend',
    title: 'Month-on-month lead to tour',
    description: 'Leads, tours booked, attended and sales by month, with the change each month.',
    prompt: 'Show leads, tours booked, tours attended and sales by month, with the change each month.',
  },
  {
    category: 'Performance',
    title: 'Best performing client',
    description: 'Which client is performing best across leads, tours and sales.',
    prompt: 'Which client is performing best?',
  },
  {
    category: 'Fix',
    title: 'Biggest opportunity',
    description: 'Where the biggest opportunity for improvement is across the business.',
    prompt: 'Where is the biggest opportunity for improvement?',
  },
  {
    category: 'Trend',
    title: 'Most improved client',
    description: 'The client that improved the most this period.',
    prompt: 'Which client improved the most this period?',
  },
  {
    category: 'Performance',
    title: 'Highest tour conversion',
    description: 'The client with the highest lead to tour conversion.',
    prompt: 'Which client has the highest tour conversion?',
  },
  {
    category: 'Performance',
    title: 'Best location across clients',
    description: 'The single best performing location across all clients.',
    prompt: 'Which location performs best across all clients?',
  },
  {
    category: 'Performance',
    title: 'Highest sales impact',
    description: 'The client where Pulse has driven the most sales.',
    prompt: 'Which client has the highest sales impact?',
  },
  {
    category: 'Fix',
    title: 'Where leads drop off',
    description: 'The point in the conversation where leads leave without booking.',
    prompt: 'At what point in the conversation do leads drop off without booking a tour?',
  },
];

const USER_PROMPTS: SuggestedPrompt[] = [
  {
    category: 'Performance',
    title: 'Results since go-live',
    description: 'Summary of leads, tours, attendance and sales since Pulse went live.',
    prompt: 'Give me a summary of leads, tours booked, tours attended and sales since go-live.',
  },
  {
    category: 'Trend',
    title: 'Best lead source',
    description: 'The lead source that performs best.',
    prompt: 'Which lead source performs best?',
  },
  {
    category: 'Fix',
    title: 'Critical knowledge gaps',
    description: 'Questions the AI could not answer that led to a lost booking, ranked by impact.',
    prompt: 'Which questions could the AI not answer that led to a lost booking? Rank them by impact.',
  },
  {
    category: 'Trend',
    title: 'Month-on-month lead to tour',
    description: 'Leads, tours booked, attended and sales by month, with the change each month.',
    prompt: 'Show leads, tours booked, tours attended and sales by month, with the change each month.',
  },
  {
    category: 'Performance',
    title: 'Best performing location',
    description: 'Which location is performing best across leads, tours and sales.',
    prompt: 'Which location is performing best?',
  },
  {
    category: 'Fix',
    title: 'Location needing attention',
    description: 'The location that needs the most attention right now.',
    prompt: 'Which location needs the most attention?',
  },
  {
    category: 'Trend',
    title: 'Most improved location',
    description: 'The location that improved the most this period.',
    prompt: 'Which location improved the most this period?',
  },
  {
    category: 'Performance',
    title: 'Highest tour conversion',
    description: 'The location that converts the most leads into tours.',
    prompt: 'Which location has the highest tour conversion?',
  },
  {
    category: 'Performance',
    title: 'Tour booking velocity',
    description: 'How fast leads book a tour after first contact, this month vs last.',
    prompt: 'How fast do leads book a tour after first contact? Compare this month vs last.',
  },
  {
    category: 'Trend',
    title: 'Where leads come from',
    description: 'The locations getting the most leads.',
    prompt: 'Where are we getting the most leads?',
  },
  {
    category: 'Performance',
    title: 'Out-of-hours conversions',
    description: 'Where we get the most conversions outside opening hours.',
    prompt: 'Where are we getting the most out-of-hours conversions?',
  },
  {
    category: 'Performance',
    title: 'Highest tour attendance',
    description: 'The location with the highest tour attendance.',
    prompt: 'Where are we seeing the highest tour attendance?',
  },
  {
    category: 'Fix',
    title: 'Most no-shows',
    description: 'The location with the most tour no-shows.',
    prompt: 'Which location has the most no-shows?',
  },
  {
    category: 'Fix',
    title: 'Leads needing follow-up',
    description: 'Recent leads that have not booked a tour or been contacted by staff.',
    prompt: 'Which recent leads have not booked a tour and still need a follow-up?',
  },
];

const INITIAL_COUNT = 4;

interface Props {
  isSuperowner: boolean;
  onSelect: (prompt: string) => void;
  disabled?: boolean;
}

function PromptCard({
  item,
  onClick,
  disabled,
}: {
  item: SuggestedPrompt;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="text-left bg-white border border-black/[0.06] rounded-2xl p-5 shadow-sm
        transition-all duration-200 hover:border-indigo-200 hover:shadow-md
        disabled:opacity-60 disabled:pointer-events-none"
    >
      <span
        className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${CATEGORY_STYLES[item.category]}`}
      >
        {item.category}
      </span>
      <h4 className="mt-3 text-[15px] font-semibold text-[#111827]">{item.title}</h4>
      <p className="mt-1.5 text-sm text-[#6b7280] leading-relaxed">{item.description}</p>
    </button>
  );
}

export default function SuggestedPrompts({ isSuperowner, onSelect, disabled }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const prompts = isSuperowner ? SUPEROWNER_PROMPTS : USER_PROMPTS;

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Suggested
        </span>
        {prompts.length > INITIAL_COUNT && (
          <button
            type="button"
            className="text-xs text-indigo-500 hover:text-indigo-600 whitespace-nowrap"
            onClick={() => setDialogOpen(true)}
          >
            More prompts
          </button>
        )}
      </div>
      <ul>
        {prompts.slice(0, INITIAL_COUNT).map((p) => (
          <li key={p.title}>
            <button
              type="button"
              title={p.description}
              disabled={disabled}
              onClick={() => onSelect(p.prompt)}
              className="w-full flex items-center gap-2 rounded-[0.6rem] px-2.5 py-2 mb-1 text-left
                transition-all duration-200 ease-in hover:bg-white/80 hover:ring-1 ring-slate-200
                hover:shadow-[0px_2px_3px_#0000000b] disabled:opacity-60 disabled:pointer-events-none"
            >
              <span
                className={`flex-none text-[10px] font-medium px-1.5 py-0.5 rounded-full ${CATEGORY_STYLES[p.category]}`}
              >
                {p.category}
              </span>
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-slate-600">
                {p.title}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <Dialog size="xl" open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <Dialog.Panel className="p-6">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="p-0 border-none text-lg font-semibold text-[#111827]">
              Suggested prompts
            </Dialog.Title>
            <button
              type="button"
              aria-label="Close"
              className="text-[#6b7280] hover:text-[#111827]"
              onClick={() => setDialogOpen(false)}
            >
              <Lucide icon="X" className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[65vh] overflow-y-auto">
            {prompts.map((p) => (
              <PromptCard
                key={p.title}
                item={p}
                disabled={disabled}
                onClick={() => {
                  setDialogOpen(false);
                  onSelect(p.prompt);
                }}
              />
            ))}
          </div>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
}
